import { clsx } from 'clsx'
import { useLayoutEffect } from 'react'
import { setLanguage, t, useLanguage } from '@/lib/i18n'

/** Each option is written in its own language, in both interface languages. */
const ENGLISH_LABEL = 'English'
const ARABIC_LABEL = '\u0627\u0644\u0639\u0631\u0628\u064A\u0629'

export interface LanguageSwitchProps {
  /**
   * `menu` is the full-width segmented control inside the avatar panel; `header` is the compact one; `text` is the single
   * text button of the public and auth headers (APP-DESIGN-DIRECTION 4.4): it names the other language, in that language.
   */
  variant: 'menu' | 'header' | 'text'
  className?: string
}

/**
 * Two options, "English" and "العربية", each written in its own language in both interface languages (so a student who
 * cannot read the current one can still find theirs), with `lang` set on each. It calls the existing `setLanguage` and
 * keeps `<html lang dir>` in step, the same as `components/shell/LanguageToggle.tsx` (that component is also mounted on
 * every non-exam route; this effect is a deliberate 4-line copy rather than an edit of a file the exam route renders).
 */
export function LanguageSwitch({ variant, className }: LanguageSwitchProps) {
  const language = useLanguage()
  useLayoutEffect(() => {
    document.documentElement.lang = language
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr'
  }, [language])
  if (variant === 'text') {
    const next = language === 'ar' ? 'en' : 'ar'
    return (
      <button type="button" className={clsx('r5-langswitch--text', className)} lang={next} onClick={() => setLanguage(next)}>
        {next === 'ar' ? ARABIC_LABEL : ENGLISH_LABEL}
      </button>
    )
  }
  return (
    <div className={clsx('r5-langswitch', `r5-langswitch--${variant}`, className)} role="group" aria-label={t('Language')}>
      <button type="button" lang="en" aria-pressed={language === 'en'} onClick={() => setLanguage('en')}>{ENGLISH_LABEL}</button>
      <button type="button" lang="ar" aria-pressed={language === 'ar'} onClick={() => setLanguage('ar')}>{ARABIC_LABEL}</button>
    </div>
  )
}
